import { Controller } from "@hotwired/stimulus"

// Contact review keyboard flow (design A8, rebuilt 2026-07-28):
// select-then-CONFIRM. 1-9 selects a candidate (digits re-select freely) and
// swaps its photo into the compare strip; the chooser then shows the ONE
// valid save key - I for Instagram handles, M for emails - derived from the
// candidate, never a quiz. Esc cancels. Enter NEVER accepts.
//
// The safety engineering points at the destructive key now: R must be
// pressed TWICE within 3 seconds (a single R used to permanently retire a
// playlist from the pipeline). D arms the dead-end chooser, then A/B/P/X
// picks the attributed reason. Research launchers (S L G Y N F T) open
// prefilled searches in new tabs; O opens the owner-vs-candidate pair;
// E jumps to manual entry. ArrowRight = skip, Z = undo. Server forms are
// the source of truth; this controller only routes keys to requestSubmit().
export default class extends Controller {
  static targets = ["candidate", "acceptForm", "chooser", "chooserLabel",
    "keyInstagram", "keyEmail", "rejectForm", "rejectButton", "skip", "undo",
    "compareSlot", "compareLabel", "launcher", "manualHandle",
    "deadend", "deadendForm"]

  ARM_WINDOW_MS = 3000

  connect() {
    this.selected = null
    this.rejectArmed = false
    this.deadendArmed = false
    this.keydown = this.handleKey.bind(this)
    document.addEventListener("keydown", this.keydown)
    if (this.hasRejectButtonTarget) this.rejectLabel = this.rejectButtonTarget.textContent
  }

  disconnect() {
    document.removeEventListener("keydown", this.keydown)
    clearTimeout(this.armTimer)
  }

  handleKey(event) {
    // Never steal keys from any editable surface (inputs, selects, and
    // contenteditable alike - manual entry lives on this screen now).
    if (event.target.closest("input, textarea, select, [contenteditable]")) return
    if (document.activeElement && document.activeElement.isContentEditable) return
    if (event.metaKey || event.ctrlKey || event.altKey) return

    const key = event.key
    if (key === "Enter") {
      // Enter NEVER accepts (design A8) - even via a focused Accept button.
      if (event.target.closest(".crq-accept-form, .crq-actions")) event.preventDefault()
      return
    }

    // A dead-end arm redirects the next key to the reason chooser.
    if (this.deadendArmed && this.pickDeadendReason(key)) { event.preventDefault(); return }

    if (key >= "1" && key <= "9") {
      this.select(parseInt(key, 10))
      event.preventDefault()
    } else if (key === "Escape") {
      this.clearSelection()
      this.disarmAll()
    } else if (this.selected !== null && (key === "i" || key === "I")) {
      this.confirm("sm")
      event.preventDefault()
    } else if (this.selected !== null && (key === "m" || key === "M")) {
      this.confirm("email")
      event.preventDefault()
    } else if (key === "r" || key === "R") {
      this.pressReject()
      event.preventDefault()
    } else if (key === "d" || key === "D") {
      this.armDeadend()
      event.preventDefault()
    } else if (key === "o" || key === "O") {
      this.openPair()
      event.preventDefault()
    } else if (key === "e" || key === "E") {
      if (this.hasManualHandleTarget) { this.manualHandleTarget.focus(); event.preventDefault() }
    } else if (key === "ArrowRight") {
      if (this.hasSkipTarget) this.skipTarget.requestSubmit()
    } else if (key === "z" || key === "Z") {
      if (this.hasUndoTarget) this.undoTarget.requestSubmit()
    } else {
      this.launch(key.toUpperCase())
    }
  }

  select(n) {
    const row = this.candidateTargets.find(el => parseInt(el.dataset.index, 10) === n)
    if (!row) return
    this.clearSelection()
    this.selected = n
    row.classList.add("crq-selected")
    const field = row.dataset.field
    this.chooserTarget.hidden = false
    this.chooserLabelTarget.textContent = `#${n} selected -`
    // Only the valid key for this candidate's derived field is live.
    this.keyInstagramTarget.classList.toggle("crq-key-disabled", field !== "sm")
    this.keyEmailTarget.classList.toggle("crq-key-disabled", field !== "email")
    this.swapCompare(row)
  }

  // The selected candidate's photo lands beside the Spotify photo at the
  // same size - the same-person call happens in place, not in a tab.
  swapCompare(row) {
    if (!this.hasCompareSlotTarget) return
    const img = row.querySelector(".crq-cand-avatar img")
    this.compareSlotTarget.innerHTML = ""
    if (img && img.style.display !== "none") {
      const clone = img.cloneNode(false)
      clone.className = "crq-photo-lg"
      this.compareSlotTarget.appendChild(clone)
    } else {
      const fallback = document.createElement("div")
      fallback.className = "crq-avatar-fallback crq-photo-lg crq-compare-empty"
      fallback.textContent = "no photo"
      this.compareSlotTarget.appendChild(fallback)
    }
    if (this.hasCompareLabelTarget) {
      const handle = row.querySelector(".crq-cand-body strong, .crq-cand-body .crq-handle")
      this.compareLabelTarget.textContent = handle ? handle.textContent.trim() : ""
    }
  }

  confirm(field) {
    const row = this.candidateTargets.find(el => parseInt(el.dataset.index, 10) === this.selected)
    if (!row || row.dataset.field !== field) return // wrong key for this candidate: ignore
    // Keyed lookup by candidate id (never array position - a conditional
    // render must not shift the accept onto the wrong candidate).
    const form = this.acceptFormTargets.find(f => f.dataset.candidateId === row.dataset.candidateId)
    if (form) form.requestSubmit()
  }

  // Reject-all requires two presses inside the arm window: it is the one
  // action on the JUDGE card that retires it, so it must never be a reflex.
  pressReject() {
    if (!this.hasRejectFormTarget) return
    if (this.rejectArmed) {
      this.rejectFormTarget.requestSubmit()
      return
    }
    this.disarmAll()
    this.rejectArmed = true
    this.rejectButtonTarget.classList.add("crq-armed")
    this.rejectButtonTarget.textContent = "Press R again to reject all"
    this.armTimer = setTimeout(() => this.disarmAll(), this.ARM_WINDOW_MS)
  }

  armDeadend() {
    if (!this.hasDeadendTarget) return
    this.disarmAll()
    this.deadendArmed = true
    this.deadendTarget.classList.add("crq-armed")
    this.deadendTarget.scrollIntoView({ block: "nearest" })
    this.armTimer = setTimeout(() => this.disarmAll(), this.ARM_WINDOW_MS)
  }

  pickDeadendReason(key) {
    const wanted = key.toUpperCase()
    const form = this.deadendFormTargets.find(f => {
      const btn = f.querySelector("[data-reason-key]")
      return btn && btn.dataset.reasonKey === wanted
    })
    if (!form) return false
    form.requestSubmit()
    return true
  }

  disarmAll() {
    clearTimeout(this.armTimer)
    if (this.rejectArmed && this.hasRejectButtonTarget) {
      this.rejectButtonTarget.classList.remove("crq-armed")
      this.rejectButtonTarget.textContent = this.rejectLabel
    }
    if (this.deadendArmed && this.hasDeadendTarget) this.deadendTarget.classList.remove("crq-armed")
    this.rejectArmed = false
    this.deadendArmed = false
  }

  // One keypress, one prefilled research tab.
  launch(key) {
    const anchor = this.launcherTargets.find(a => a.dataset.key === key)
    if (anchor) anchor.click()
  }

  // The pair that settles a photo call: the candidate's live profile and
  // the owner's Spotify profile, both in background tabs.
  openPair() {
    const row = this.candidateTargets.find(el => parseInt(el.dataset.index, 10) === this.selected)
    if (row && row.dataset.profileUrl) window.open(row.dataset.profileUrl, "_blank", "noopener")
    const spotify = this.launcherTargets.find(a => a.dataset.key === "S")
    if (spotify) window.open(spotify.href, "_blank", "noopener")
  }

  clearSelection() {
    this.selected = null
    this.candidateTargets.forEach(el => el.classList.remove("crq-selected"))
    if (this.hasChooserTarget) this.chooserTarget.hidden = true
  }
}
